import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Star, RefreshCw, Zap, Trophy, Search, ChevronDown } from 'lucide-react';
import { API } from '../App';
import Header from '../components/layout/Header';

const SORT_OPTIONS = [
  { id: 'popular', label: 'Most Popular' },
  { id: 'rated', label: 'Highest Rated' },
  { id: 'reruns', label: 'Most Re-runs' },
  { id: 'newest', label: 'Newest' },
];

const DECISION_LABELS = {
  knockout: { text: 'KNOCKOUT', color: '#EF4444' },
  split_decision: { text: 'SPLIT', color: '#F59E0B' },
  unanimous: { text: 'UNANIMOUS', color: '#22C55E' },
  draw: { text: 'DRAW', color: '#A0A0A0' },
};

export default function CatFightGallery() {
  const navigate = useNavigate();
  const [fights, setFights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('popular');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadFights();
  }, [sort, page]);

  const loadFights = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort, page, limit: 20 });
      if (search) params.set('search', search);
      const res = await API.get(`/api/gallery/cat-fights?${params}`);
      setFights(res.data.fights || []);
      setTotalPages(res.data.pages || 1);
    } catch {}
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadFights();
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold font-['Barlow_Condensed']">Cat Fight Gallery</h1>
            <p className="text-sm text-[#A0A0A0] mt-1">Watch, re-run, and rate the best AI arguments</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/leaderboard/cat-fights" className="px-3 py-1.5 min-h-[44px] border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-mono flex items-center gap-1 hover:bg-[#F59E0B]/10">
              <Trophy className="w-3 h-3" /> Leaderboard
            </Link>
            <Link to="/fray/custom" className="px-3 py-1.5 min-h-[44px] bg-[#EF4444] text-white text-xs font-mono flex items-center gap-1">
              <Zap className="w-3 h-3" /> Start a Fight
            </Link>
          </div>
        </div>

        {/* Search + Sort */}
        <div className="flex gap-3 mb-6">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="flex-1 relative">
              <Search className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#EF4444]"
                placeholder="Search topics..." />
            </div>
          </form>
          <div className="flex gap-1">
            {SORT_OPTIONS.map(s => (
              <button key={s.id} onClick={() => { setSort(s.id); setPage(1); }}
                className={`px-3 py-2 text-[10px] font-mono border ${sort === s.id ? 'bg-[#EF4444] text-white border-[#EF4444]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#EF4444]/30'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Fight Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#EF4444] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : fights.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#A0A0A0] text-sm">No published Cat Fights yet. Be the first!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fights.map((fight, i) => {
              const dec = DECISION_LABELS[fight.decision] || DECISION_LABELS.draw;
              return (
                <motion.div key={fight.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/gallery/cat-fights/${fight.id}`}
                    className="block p-4 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#EF4444]/30 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-semibold font-['Barlow_Condensed'] flex-1 mr-2">{fight.topic}</h3>
                      <span className="text-[8px] px-1.5 py-0.5 border font-mono shrink-0"
                        style={{ color: dec.color, borderColor: dec.color + '40' }}>{dec.text}</span>
                    </div>
                    {fight.winner && fight.winner !== 'Draw' && (
                      <p className="text-xs text-[#EF4444] mb-2">Winner: {fight.winner}</p>
                    )}
                    <div className="flex items-center gap-3 text-[9px] font-mono text-[#A0A0A0]">
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {fight.view_count}</span>
                      <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {fight.avg_rating || '—'} ({fight.rating_count})</span>
                      <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3" /> {fight.rerun_count}</span>
                      <span>{fight.rounds_completed} rounds</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[9px] text-[#A0A0A0] font-mono">{fight.voice_names?.join(' vs ')}</span>
                      <span className="text-[9px] text-[#A0A0A0] font-mono">by {fight.creator}</span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 border border-[#2A2A2A] text-xs font-mono text-[#A0A0A0] disabled:opacity-30">Prev</button>
            <span className="text-xs font-mono text-[#A0A0A0] py-1">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}
              className="px-3 py-1 border border-[#2A2A2A] text-xs font-mono text-[#A0A0A0] disabled:opacity-30">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
